import { GridApi, IFilterParams } from 'ag-grid-community';
import {
  Box,
  Center,
  Checkbox,
  CloseButton,
  Group,
  Popover,
  Stack,
  TextInput,
  Tooltip,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import {
  buildPermissionFilterOptions,
  filterPermissionFilterOptions,
  normalizeUserPermissionsFilterValue,
  UserPermissionsFilterModel,
  userHasSelectedPermissionsAnywhere,
} from 'components/tables/filters/UserPermissionsFilter.helpers';
import { UserRow } from 'components/tables/data/user/types';
import { usePermissionDataProvider } from 'model/PermissionDataContext';
import { isEqual, noop } from 'lodash';
import P2Regular from 'components/typography/P2Regular';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import P3Medium from 'components/typography/P3Medium';
import P3Regular from 'components/typography/P3Regular';
import { P3Medium as P3MediumTypography } from 'theme/typography';

export const FILTER_KEY = 'permissions' as const;
const ACTIVE_LABEL_LIMIT = 2;

/**
 * Narrows maybe-missing permission options after lookups by slug.
 */
function isPermissionFilterOption(option: PermissionFilterOption | undefined): option is PermissionFilterOption {
  return option !== undefined;
}

interface UserPermissionsFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
}

interface UserPermissionsFilterRef {
  onParentModelChanged: () => void;
}

interface PermissionFilterOption {
  value: string;
  label: string;
  group: string;
}

interface SelectedPermissionPillProps {
  pillId: string;
  label: string;
  maxWidth: number;
}

/**
 * Selected-permission pill that shows a tooltip only when its label is truncated.
 */
function SelectedPermissionPill({ pillId, label, maxWidth }: SelectedPermissionPillProps) {
  const [isTruncated, setIsTruncated] = useState(false);
  const labelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    /**
     * Updates whether the pill label is visually truncated.
     */
    const updateTruncation = () => {
      const element = labelRef.current;

      if (!element) {
        /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
        return;
      }

      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    updateTruncation();
    window.addEventListener('resize', updateTruncation);

    return () => window.removeEventListener('resize', updateTruncation);
  }, [label, maxWidth]);

  return (
    <Tooltip withinPortal disabled={!isTruncated} label={label}>
      <Box
        px={6}
        py={0}
        bg="gray.1"
        sx={{
          borderRadius: 999,
          border: '1px solid #E9ECEF',
          maxWidth,
          minWidth: 0,
          flexShrink: 1,
        }}
      >
        <Box
          ref={labelRef}
          data-testid={`selected-permission-pill-label-${pillId}`}
          sx={{
            ...P3MediumTypography,
            display: 'block',
            minWidth: 0,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * AG Grid parent filter for matching users who have every selected permission anywhere.
 */
export const UserPermissionsColumnFilter = forwardRef(({ filterChangedCallback }: IFilterParams, ref) => {
  const value = useRef<string[]>([]);

  useImperativeHandle(ref, () => ({
    doesFilterPass({ data }: { data: UserRow }) {
      return userHasSelectedPermissionsAnywhere(data, value.current);
    },

    getModel() {
      if (value.current.length === 0) {
        return null;
      }

      return { filter: value.current };
    },

    setModel(model: UserPermissionsFilterModel | null) {
      value.current = normalizeUserPermissionsFilterValue(model?.filter);
    },

    isFilterActive() {
      return value.current.length > 0;
    },

    onFloatingFilterChanged(newValue: string[]) {
      if (!isEqual(value.current, newValue)) {
        value.current = normalizeUserPermissionsFilterValue(newValue);
        filterChangedCallback();
      }
    },
  }));

  return <></>;
});

/**
 * Floating filter for filtering users by permissions.anywhere using AND semantics across selected slugs.
 */
const UserPermissionsFilter = forwardRef<UserPermissionsFilterRef, UserPermissionsFilterProps>(
  function UserPermissionsFilter({ api, onChange }, ref) {
    const theme = useMantineTheme();
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const { permissions } = usePermissionDataProvider();
    const [value, setValue] = useState<string[]>(() =>
      normalizeUserPermissionsFilterValue(api.getFilterModel()[FILTER_KEY]?.filter)
    );
    const [search, setSearch] = useState('');
    const [opened, setOpened] = useState(false);

    const options = useMemo(() => buildPermissionFilterOptions(permissions), [permissions]);
    const filteredOptions = useMemo(() => filterPermissionFilterOptions(options, search), [options, search]);
    const optionMap = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
    const groupedOptions = useMemo(() => {
      const groups = new Map<string, typeof filteredOptions>();

      filteredOptions.forEach((option) => {
        if (!groups.has(option.group)) {
          groups.set(option.group, []);
        }

        groups.get(option.group)!.push(option);
      });

      return Array.from(groups.entries());
    }, [filteredOptions]);
    const selectedOptions = useMemo(
      () => value.map((slug) => optionMap.get(slug)).filter(isPermissionFilterOption),
      [optionMap, value]
    );

    useImperativeHandle(ref, () => ({ onParentModelChanged: noop }));

    const pushModel = useCallback(
      (nextValue: string[]) => {
        const normalizedValue = normalizeUserPermissionsFilterValue(nextValue);
        const nextModel = normalizedValue.length > 0 ? { filter: normalizedValue } : null;

        if (onChange) {
          onChange({ [FILTER_KEY]: nextModel });
          return;
        }

        api.setFilterModel({
          ...api.getFilterModel(),
          [FILTER_KEY]: nextModel,
        });
      },
      [api, onChange]
    );

    const togglePermission = useCallback(
      (permissionSlug: string) => {
        const nextValue = value.includes(permissionSlug)
          ? value.filter((currentSlug) => currentSlug !== permissionSlug)
          : [...value, permissionSlug];

        setValue(nextValue);
        pushModel(nextValue);
      },
      [pushModel, value]
    );

    const clearFilter = useCallback(() => {
      setValue([]);
      pushModel([]);
    }, [pushModel]);

    /**
     * Opens the dropdown.
     */
    const openDropdown = useCallback(() => {
      setOpened(true);
    }, []);

    useEffect(() => {
      /**
       * Keeps the floating filter in sync with external grid model updates.
       */
      const handleFilterChanged = ({ api: gridApi }: { api: GridApi }) => {
        setValue(normalizeUserPermissionsFilterValue(gridApi.getFilterModel()[FILTER_KEY]?.filter));
      };

      api.addEventListener('filterChanged', handleFilterChanged);
      return () => api.removeEventListener('filterChanged', handleFilterChanged);
    }, [api]);

    useEffect(() => {
      if (opened) {
        window.requestAnimationFrame(() => {
          searchInputRef.current?.focus();
        });
      }
    }, [opened]);

    return (
      <Popover
        withinPortal
        width={400}
        opened={opened}
        onChange={setOpened}
        closeOnEscape
        returnFocus={false}
        shadow="sm"
      >
        <Popover.Target>
          <Box
            role="button"
            aria-label="Filter oprávnení"
            w="100%"
            h={32}
            py={4}
            px={8}
            bg="white"
            tabIndex={0}
            onClick={openDropdown}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) {
                return;
              }

              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openDropdown();
              }
            }}
            sx={{
              borderRadius: rem(4),
              border: `1px solid ${theme.colors.gray[4]}`,
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              '&:hover': {
                borderColor: theme.colors.red[8],
                cursor: 'text',
              },
              '&:focus-within': {
                borderColor: theme.colors.red[8],
              },
            }}
          >
            <Group noWrap spacing={4} sx={{ flex: 1, minWidth: 0, paddingRight: value.length > 0 ? rem(36) : 0 }}>
              {selectedOptions.slice(0, ACTIVE_LABEL_LIMIT).map((option) => (
                <SelectedPermissionPill
                  key={option.value}
                  pillId={option.value}
                  label={option.label}
                  maxWidth={selectedOptions.length > 1 ? 96 : 180}
                />
              ))}
              {selectedOptions.length === 0 && <P2Regular color="gray.5">Všetky oprávnenia</P2Regular>}
              {selectedOptions.length > ACTIVE_LABEL_LIMIT && (
                <Tooltip
                  withinPortal
                  label={
                    <Stack spacing={6} py={4}>
                      {selectedOptions.slice(ACTIVE_LABEL_LIMIT).map((option) => (
                        <P2Regular key={option.value} color="white">
                          {option.label}
                        </P2Regular>
                      ))}
                    </Stack>
                  }
                >
                  <Center w={24} h={24} bg="gray.1" sx={{ transform: 'translateY(-1px)', borderRadius: '50%' }}>
                    <P3Medium color="gray.7">+{selectedOptions.length - ACTIVE_LABEL_LIMIT}</P3Medium>
                  </Center>
                </Tooltip>
              )}
            </Group>
            {value.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: rem(28),
                  '& button': {
                    paddingLeft: 0,
                    paddingRight: 0,
                    opacity: 1,
                    marginRight: rem(-12),
                    '& svg': {
                      width: `${rem(16)} !important`,
                      height: `${rem(16)} !important`,
                    },
                  },
                }}
              >
                <CloseButton
                  variant="transparent"
                  size="sm"
                  title="Vymazať všetky vybrané oprávnenia"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    clearFilter();
                  }}
                  aria-label="Vymazať všetky vybrané oprávnenia"
                />
              </Box>
            )}
          </Box>
        </Popover.Target>
        <Popover.Dropdown p={0} sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
          <Box
            px={10}
            py={6}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: theme.white,
              borderBottom: `1px solid ${theme.colors.gray[2]}`,
            }}
          >
            <TextInput
              ref={searchInputRef}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              placeholder="Vyhľadať oprávnenie"
              aria-label="Vyhľadať oprávnenie"
              size="xs"
              icon={<IconSearch size={14} stroke={1.75} />}
            />
          </Box>
          {groupedOptions.length === 0 && (
            <Box px={10} py={8}>
              <P3Regular color="gray.6">Žiadne oprávnenia sa nenašli</P3Regular>
            </Box>
          )}
          {groupedOptions.map(([groupName, groupOptions]) => (
            <React.Fragment key={groupName}>
              <Box px={10} pt={8} pb={4}>
                <P3Medium color="gray.7">{groupName}</P3Medium>
              </Box>
              {groupOptions.map((option) => (
                <Box
                  key={option.value}
                  onClick={() => togglePermission(option.value)}
                  sx={{
                    padding: `${rem(6)} ${rem(10)}`,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.colors.gray[0],
                    },
                  }}
                >
                  <Group spacing={8} noWrap align="center">
                    <Checkbox
                      size="sm"
                      styles={{ input: { cursor: 'pointer !important' } }}
                      checked={value.includes(option.value)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => togglePermission(option.value)}
                    />
                    <P3Regular>{option.label}</P3Regular>
                  </Group>
                </Box>
              ))}
            </React.Fragment>
          ))}
        </Popover.Dropdown>
      </Popover>
    );
  }
);

export default UserPermissionsFilter;
